import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { verifyAuth, getStats, seedDatabase } from '../../lib/api';
import { toast } from 'sonner';
import { Toaster } from '../ui/sonner';
import {
  LayoutDashboard,
  Package,
  Tags,
  Building2,
  Users,
  FileText,
  HelpCircle,
  LogOut,
  Menu,
  X,
  Home,
  Database,
} from 'lucide-react';
import { LOGO_SRC } from '../../lib/branding';

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    verifyAuth()
      .then(() => {
        getStats().then(res => setStats(res.data)).catch(console.error);
      })
      .catch(() => {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const handleSeedDatabase = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      await seedDatabase();
      toast.success('Database seeded successfully');
      getStats().then(res => setStats(res.data));
    } catch (error) {
      toast.error('Failed to seed database');
    } finally {
      setSeeding(false);
    }
  };

  const navItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Categories', path: '/admin/categories', icon: Tags },
    { name: 'Brands', path: '/admin/brands', icon: Building2 },
    { name: 'Products', path: '/admin/products', icon: Package },
    { name: 'Leads', path: '/admin/leads', icon: Users },
    { name: 'Blogs', path: '/admin/blogs', icon: FileText },
    { name: 'FAQs', path: '/admin/faqs', icon: HelpCircle },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#F7F6F3]" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 admin-sidebar transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-700">
            <img src={LOGO_SRC} alt="Passion of Aroma" className="h-10" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className={`admin-nav-item flex items-center gap-3 px-4 py-3 text-sm ${
                      isActive(item.path)
                        ? 'active text-[#D4AF37]'
                        : 'text-gray-300 hover:text-white'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`admin-nav-${item.name.toLowerCase()}`}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Seed Button */}
            <div className="mt-8 px-4">
              <button
                onClick={handleSeedDatabase}
                disabled={seeding}
                className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#D4AF37] transition-colors w-full"
                data-testid="seed-database-btn"
              >
                <Database className="h-4 w-4" />
                {seeding ? 'Seeding...' : 'Seed Demo Data'}
              </button>
            </div>
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-700">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-white"
              data-testid="admin-view-site"
            >
              <Home className="h-5 w-5" />
              View Site
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-red-400 w-full"
              data-testid="admin-logout"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2"
              data-testid="admin-menu-toggle"
            >
              {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            <div className="flex items-center gap-4 ml-auto">
              {stats && (
                <div className="hidden md:flex gap-6 text-sm">
                  <span className="text-gray-500">
                    <span className="font-medium text-[#0B1F3B]">{stats.products}</span> Products
                  </span>
                  <span className="text-gray-500">
                    <span className="font-medium text-[#0B1F3B]">{stats.leads}</span> Leads
                  </span>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet context={{ stats, refreshStats: () => getStats().then(res => setStats(res.data)) }} />
        </main>
      </div>

      <Toaster position="top-right" />
    </div>
  );
}
